import { singleton, inject } from "tsyringe";

// 임시: 테이블 모델
import type { TableDataSource } from "@/shared/types/adapter/interface";

// shared
import type { IDateAdapter } from "@/shared/libs/date/interface";

// entities
import type { StudyDto } from "@/entities/study/models/dtos";
import { StudyBehavior } from "@/entities/study/models/behavior/StudyBehavior";
import { AttendanceBehavior } from "@/entities/attendance/domain/behaviors/AttendanceBehavior";

// pages
import { AchievementRankConverter } from "@/pages/student-setting/models/converter/AchievementRankConverter";

export interface StudentLatestStudyRecordConverterStructure {
    convert: (studyDtos: StudyDto[]) => TableDataSource[];
}

@singleton()
export class StudentLatestStudyRecordConverter {
    constructor(
        @inject(StudyBehavior)
        private readonly studyBehavior: StudyBehavior,
        @inject(AttendanceBehavior)
        private readonly attendanceBehavior: AttendanceBehavior,
        @inject(AchievementRankConverter)
        private readonly achievementRankConverter: AchievementRankConverter,
        @inject("DateAdapter")
        private readonly dateAdapter: IDateAdapter,
    ) {}

    public convert = (studyDtos: StudyDto[]) => {
        const tableBody: TableDataSource[] = studyDtos.map((studyDto) => {
            const body = {
                id: studyDto.id,
                date: "-",
                answerRate: "0%",
                totalProblems: "0개",
                elapsedTime: "0분",
                rank: "-",
                o2x2deficient: "-",
            };

            // 시작 날짜 계산
            const attendance = studyDto.attendance;
            if (!this.attendanceBehavior.isAbsence(attendance)) {
                const startedAt = attendance.startedAt;

                if (startedAt) {
                    body.date = this.dateAdapter.formatZoned(startedAt, "Asia/Seoul", "yyyy-MM-dd");
                }
            }

            const summary = studyDto.summary;

            if (summary) {
                // 정답률
                const correctCount = this.studyBehavior.getCorrectCount(summary);
                const submittedCount = this.studyBehavior.getSubmittedCount(summary);

                if (submittedCount > 0) {
                    body.answerRate = `${Math.round((correctCount / submittedCount) * 100)}%`;
                }

                // 전체 문항 수
                body.totalProblems = `${submittedCount}개`;

                // 순공 시간
                const elapsedTime = this.studyBehavior.getElapsedTime(summary);
                body.elapsedTime = `${Math.floor(elapsedTime / 60)}분`;

                // 성취도 랭크
                const rank = summary.achievement.rank;
                body.rank = this.achievementRankConverter.convert(rank);

                // O2/X2/부족 수치 계산
                const correctedAfterWrongCount =
                    this.studyBehavior.getCorrectedAfterWrongCount(summary);
                const wrongInRepeatedCount = this.studyBehavior.getWrongInRepeatedCount(summary);
                const selfEvaluatedAsWorseCount =
                    this.studyBehavior.getSelfEvaluatedAsWorseCount(summary);

                body.o2x2deficient = `${correctedAfterWrongCount}/${wrongInRepeatedCount}/${selfEvaluatedAsWorseCount}`;
            }

            return body;
        });

        return tableBody;
    };
}
